import type {Thread} from '@github-ui/diff-lines/conversation-types'
import type {IssueComment} from '../../../page-data/payloads/issue-comments'
import type {ReviewSummary} from '../../../page-data/payloads/review-summaries'
import type {SafeHTMLString} from '@github-ui/safe-html'
import type {ConversationItem} from './build-conversation-items'

/**
 * Adapts a ReviewSummary to the IssueComment shape so it can be used
 * for filtering with useFilterIssueCommentsIds. The adapter maps:
 *  - submittedAt → createdAt / publishedAt
 *  - repository is non-nullable on ReviewSummary, assignable to nullable
 */
function reviewSummaryToIssueComment(summary: ReviewSummary): IssueComment {
  return {
    id: summary.id,
    authorAssociation: summary.authorAssociation,
    authorAvatarUrl: summary.authorAvatarUrl,
    authorLogin: summary.authorLogin,
    body: summary.body,
    bodyHtml: summary.bodyHtml,
    createdAt: summary.submittedAt,
    isHidden: summary.isHidden,
    minimizedReason: summary.minimizedReason,
    publishedAt: summary.submittedAt,
    reactionGroups: summary.reactionGroups,
    repository: summary.repository,
    url: summary.url,
    viewerCanReact: summary.viewerCanReact,
  }
}

/**
 * Adapts a `Thread` to the `IssueComment` shape so it can be used for
 * filtering with `useFilterIssueCommentsIds`.
 *
 * Returns `null` for empty threads.
 */
function threadToIssueComment(thread: Thread): IssueComment | null {
  const firstComment = thread.commentsData?.comments?.[0]
  if (!firstComment) return null
  return {
    id: thread.id,
    authorAssociation: firstComment.authorAssociation,
    authorAvatarUrl: firstComment.author?.avatarUrl ?? '',
    authorLogin: firstComment.author?.login ?? '',
    body: firstComment.body,
    // TODO: This should be SafeHTMLString at the Comment model definition
    // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string
    bodyHtml: firstComment.bodyHTML as SafeHTMLString,
    createdAt: firstComment.createdAt,
    isHidden: firstComment.isHidden,
    minimizedReason: firstComment.minimizedReason ?? null,
    publishedAt: firstComment.publishedAt ?? null,
    reactionGroups: firstComment.reactionGroups ?? [],
    repository: firstComment.repository,
    url: firstComment.url,
    viewerCanReact: firstComment.viewerCanReact ?? false,
  }
}

/**
 * Align conversation items to the issue comment model for filtering.
 */
export function conversationItemToIssueComment(item: ConversationItem): IssueComment | null {
  switch (item.type) {
    case 'issue-comment':
      return item.data
    case 'review-summary':
      return reviewSummaryToIssueComment(item.data.summary)
    case 'thread':
      return threadToIssueComment(item.data)
  }
}
